/**
 * Stops the Findter Filter & Search app's "Vis mere" (load more) button from
 * shifting the page.
 *
 * The app appends the next page ~800ms after the click. Layout shifts within
 * 500ms of an input are excused from CLS; this one is not, so the button and
 * the footer below it jumping down counted in full (0.1-0.5 per click on
 * desktop). The app's script is served from its own CDN and cannot be changed,
 * so instead, on the click itself, the product list is given a min-height that
 * already includes the incoming rows. That growth - and the button moving down
 * with it - happens inside the 500ms window, and the products then fill space
 * that is already there. The surrounding grid gets the same reservation, so
 * the footer also stays put when the last page arrives and the button goes.
 *
 * The reservation is an estimate (rows vary a little with title length), so it
 * is never shrunk back afterwards - a few pixels of slack before the button is
 * cheaper than a shift. It is dropped when the list is replaced by a shorter
 * one (filter or sort change) or the viewport width changes.
 */
(() => {
  const LIST = 'fdt-search-product-results';
  const GRID = '.search-filters__product-grid';

  let grid = null;
  let reservedAt = 0; // product count when space was last reserved
  let observer = null;
  let viewportWidth = window.innerWidth;

  function release() {
    if (grid) {
      grid.style.minHeight = '';
      const list = grid.querySelector(LIST);
      if (list) {
        list.style.minHeight = '';
        list.style.alignContent = '';
      }
    }
    observer?.disconnect();
    grid = null;
    reservedAt = 0;
  }

  function incomingCount(button) {
    const pageSize = Number(window.fdtLimit) || 16;
    const nextPage = Number(button.dataset.page);
    const totalPages = Number(button.dataset.totalPages);
    const total = parseInt(document.querySelector('fdt-product-count')?.textContent.replace(/\D/g, ''), 10);

    // Only the last page can be short. Counting from the page number rather than `loaded` keeps this right when the
    // visitor landed on ?page=N and earlier pages are not in the list.
    if (nextPage && nextPage === totalPages && total) {
      return Math.max(0, Math.min(pageSize, total - (totalPages - 1) * pageSize));
    }

    return pageSize;
  }

  function reserve(button) {
    const target = button.closest(GRID);
    const list = target?.querySelector(LIST);
    const items = list ? Array.from(list.children) : [];

    if (!items.length) return;

    // Measure without an earlier reservation, so estimates do not pile up across clicks.
    list.style.minHeight = '';
    target.style.minHeight = '';

    const firstTop = items[0].offsetTop;
    const columns = items.filter((item) => item.offsetTop === firstTop).length || 1;
    const rows = Math.ceil(items.length / columns);
    const listHeight = list.getBoundingClientRect().height;
    const newRows = Math.ceil((items.length + incomingCount(button)) / columns) - rows;

    if (newRows <= 0) return;

    const extra = Math.ceil((newRows * listHeight) / rows);
    target.style.minHeight = `${Math.ceil(target.getBoundingClientRect().height) + extra}px`;
    // The list is a wrapping flexbox: without this, its rows would stretch into the reserved space and move.
    list.style.alignContent = 'flex-start';
    list.style.minHeight = `${Math.ceil(listHeight) + extra}px`;

    if (grid !== target) {
      observer?.disconnect();
      grid = target;
      observer = new MutationObserver(() => {
        const count = grid.querySelector(LIST)?.children.length ?? 0;
        if (!grid.isConnected || count < reservedAt) release();
      });
      observer.observe(grid, { childList: true, subtree: true });
    }

    reservedAt = items.length;
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('load-more-btn');
      if (button) reserve(button);
    },
    true
  );

  window.addEventListener('resize', () => {
    if (window.innerWidth === viewportWidth) return;
    viewportWidth = window.innerWidth;
    release();
  });
})();
